import type { EmbedContext, ResolvedAttribution } from "./types.js";
import { trackClick } from "./tracking.js";

const ARROW_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`;

/**
 * Fields the mobile link needs, satisfied by both `InlineConfig` and the
 * combination of `ModalConfig` + `ModalConfig.trigger`.
 */
export interface MobileLinkConfig {
  // Link / tracking
  shareUrl: string;
  embedApiUrl: string;
  formId: string;
  embed: EmbedContext;
  attribution: ResolvedAttribution;
  logoUrl: string;
  preview?: boolean;
  // Visual
  text: string;
  label: string;
  glass: boolean;
  variant: "default" | "primary" | "outline";
  borderRadius: number;
  fontSize: number;
  height: number;
  logoSize: number;
  colors: {
    button: string;
    buttonText: string;
    logo: string;
    label: string;
  };
}

interface MobileLinkProps {
  config: MobileLinkConfig;
  /** Open the form in a new tab. Defaults to true. */
  newTab?: boolean;
}

export function MobileLink({ config, newTab = true }: MobileLinkProps) {
  return (
    <div
      class="text-center w-full"
      style={config.glass ? {
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRadius: `calc(${config.borderRadius}px + 6px)`,
        padding: "20px",
        boxShadow: "0 8px 32px 0 rgba(31,38,135,0.15)",
      } : undefined}
    >
      {/* Label */}
      {config.label && (
        <div
          class={`font-medium ${config.variant !== "default" ? "text-center" : "text-left"} mb-2`}
          style={{
            color: config.colors.label,
            fontSize: `${config.fontSize * 0.8}px`,
          }}
        >
          {config.label}
        </div>
      )}

      {/* Link styled as button */}
      <a
        class="flex items-center no-underline w-full"
        href={config.shareUrl}
        target={newTab ? "_blank" : undefined}
        rel={newTab ? "noopener noreferrer" : undefined}
        onClick={(e) => {
          // Preview mode: stay in the builder — no navigation, no tracking.
          if (config.preview) {
            e.preventDefault();
            return;
          }
          trackClick(config.embedApiUrl, config.formId, config.embed, config.attribution);
        }}
      >
        {config.variant === "default" ? (
          <div class="relative w-full">
            <span
              class="flex items-center justify-center font-medium w-full whitespace-nowrap overflow-hidden text-ellipsis"
              style={{
                backgroundColor: config.colors.button,
                color: config.colors.buttonText,
                borderRadius: `${config.borderRadius}px`,
                fontSize: `${config.fontSize}px`,
                height: `${config.height}px`,
                paddingLeft: "12px",
                paddingRight: `${config.height + 12}px`,
              }}
            >
              {config.text}
            </span>
            <div
              class="flex items-center justify-center absolute top-0 right-0 z-[1]"
              style={{
                backgroundColor: config.colors.logo,
                borderRadius: `${config.borderRadius}px`,
                height: `${config.height}px`,
                padding: `${(config.height - config.logoSize) / 2}px`,
              }}
            >
              <img
                src={config.logoUrl}
                alt="FormVue"
                style={{ width: `${config.logoSize}px`, height: `${config.logoSize}px` }}
              />
            </div>
          </div>
        ) : (
          // Mirrors the desktop `TriggerButton` new-layout exactly so the
          // mobile link is visually identical: a full-height logo cap flush to
          // the left edge, with the text region offset by `height + 12`.
          <div class="flex items-center w-full relative">
            <div
              class="flex items-center justify-center absolute top-0 left-0 z-[3]"
              style={{
                backgroundColor: config.colors.logo,
                borderRadius: `${config.borderRadius}px`,
                height: `${config.height}px`,
                padding: `${(config.height - config.logoSize) / 2}px`,
              }}
            >
              <img
                src={config.logoUrl}
                alt="FormVue"
                class="shrink-0"
                style={{ width: `${config.logoSize}px`, height: `${config.logoSize}px` }}
              />
            </div>

            <span
              class="flex items-center justify-center font-medium overflow-hidden h-full whitespace-nowrap shrink-0 p-0"
              style={{
                backgroundColor: config.variant === "outline" ? "white" : "#0a0a0a",
                color: config.variant === "outline" ? "#0a0a0a" : "#fafafa",
                borderRadius: `${config.borderRadius}px`,
                fontFamily: "system-ui, -apple-system, sans-serif",
                height: `${config.height}px`,
                flex: "1 1 auto",
                border: config.variant === "outline" ? "1px solid #e5e5e5" : undefined,
                paddingLeft: `${config.height + 12}px`,
                gap: "16px",
              }}
            >
              <span
                class="flex items-center justify-center whitespace-nowrap flex-1"
                style={{ fontSize: `${config.fontSize}px` }}
              >
                {config.text}
              </span>
              <span
                class="flex items-center shrink-0"
                style={{ paddingRight: "16px" }}
                dangerouslySetInnerHTML={{ __html: ARROW_SVG }}
              />
            </span>
          </div>
        )}
      </a>
    </div>
  );
}
